import { useCallback, useEffect, useState } from 'react'
import type { DonjonDTO, ExpeditionDTO } from '@whatremains/shared'
import { EffetObjet, NomObjet } from '../composants/NomObjet'
import { api, ErreurApi } from '../api'
import { IconeObjet } from '../composants/IconeObjet'
import { TypeObjet } from '../composants/TypeObjet'

/**
 * Les donjons, et le codex qui dit ce qu'il y a dedans.
 *
 * L'écran a deux visages, et un seul à la fois : soit on est dans un donjon et
 * il n'y a plus qu'un étage devant soi, soit on est dehors et on lit ce qu'on
 * sait du monde.
 *
 * Le codex vient avant l'expédition dans l'ordre des choses : `07-DONJONS-ET
 * -BUTIN.md` §6 pose que **savoir est le début du voyage, pas la récompense**.
 * On voit donc la pièce nommée et son lieu bien avant de pouvoir la prendre.
 */

const NOMS_FORMAT: Record<DonjonDTO['format'], string> = {
  court: 'court',
  long: 'long',
  evenementiel: 'événementiel',
}

const NOMS_TYPE: Record<ExpeditionDTO['etage']['type'], string> = {
  rencontre: 'Rencontre',
  passage: 'Passage',
  trouvaille: 'Trouvaille',
  carrefour: 'Carrefour',
  palier: 'Palier',
  boss: 'Au bout',
}

const NOMS_STATS: Record<string, string> = {
  force: 'Force', adresse: 'Adresse', esprit: 'Esprit',
  vigueur: 'Vigueur', fortune: 'Fortune',
}

function Codex({
  donjons,
  surEntrer,
}: {
  donjons: DonjonDTO[]
  surEntrer: (d: DonjonDTO) => void
}) {
  if (donjons.length === 0) {
    return (
      <p className="attenue" style={{ maxWidth: '62ch' }}>
        Tu ne connais aucun endroit fermé. Ça se découvre en allant sur place, jamais autrement.
      </p>
    )
  }

  return (
    <div className="donjons">
      {donjons.map((d) => (
        <div className={`donjon${d.ouvert ? '' : ' clos'}`} key={d.code}>
          <div className="donjon-tete">
            <h3>{d.nom}</h3>
            <div className="donjon-meta">
              <span>{d.lieu}</span>
              <span>{d.region}</span>
              <span>niveau {d.niveauConseille}</span>
              <span>{d.etages} étages</span>
              <span>{NOMS_FORMAT[d.format]}</span>
              {d.acheve && <span className="fait">déjà mené au bout</span>}
            </div>
          </div>

          <p className="donjon-raison">{d.raison}</p>

          {d.butin && (
            <div className={`piece-nommee${d.butin.obtenu ? ' prise' : ''}`}>
              <span className="etiquette">
                {d.butin.obtenu ? 'Tu l’as' : 'Ce qu’il y a dedans'}
              </span>
              <NomObjet o={d.butin} />
              <i>{d.butin.texte}</i>
              <span className="donjon-meta">
                <span>niveau {d.butin.niveauRequis}</span>
                <span>{d.butin.palier}</span>
                {!d.butin.obtenu && <span>elle tombe à coup sûr</span>}
              </span>
            </div>
          )}

          <div className="donjon-pied">
            {d.format === 'evenementiel' && d.minutesAvantFermeture !== null && (
              <span className="urgence">
                {d.ouvert
                  ? `se referme dans ${Math.floor(d.minutesAvantFermeture / 60)} h`
                  : 'refermé'}
              </span>
            )}
            <button
              className="action"
              disabled={!d.ici || !d.ouvert}
              onClick={() => surEntrer(d)}
            >
              {!d.ouvert ? 'Refermé' : d.ici ? 'Descendre' : 'Il faut y être'}
            </button>
          </div>
        </div>
      ))}
    </div>
  )
}

export function Donjons({
  id,
  surChangement,
  surCombat,
}: {
  id: string
  surChangement: () => void
  surCombat: () => void
}) {
  const [donjons, setDonjons] = useState<DonjonDTO[]>([])
  const [expedition, setExpedition] = useState<ExpeditionDTO | null>(null)
  const [erreur, setErreur] = useState<string | null>(null)
  const [note, setNote] = useState<string | null>(null)

  const recharger = useCallback(async () => {
    const [d, e] = await Promise.all([api.donjons(id), api.expedition(id)])
    setDonjons(d)
    setExpedition(e)
  }, [id])

  useEffect(() => { void recharger() }, [recharger])

  async function agir(action: () => Promise<unknown>, dire?: (r: unknown) => string | null) {
    setErreur(null)
    try {
      const r = await action()
      setNote(dire ? dire(r) : null)
      await recharger()
      surChangement()
    } catch (e) {
      setNote(null)
      setErreur(e instanceof ErreurApi ? e.message : "Quelque chose n'a pas abouti.")
    }
  }

  // ─── Dehors ───────────────────────────────────────────────────────────────

  if (!expedition) {
    return (
      <div className="cadre">
        <div className="entete">
          <div className="surtitre">Codex</div>
          <h1>Ce qui est fermé</h1>
          <p className="attenue" style={{ maxWidth: '64ch' }}>
            Ce que tu sais des endroits qu’on ne traverse pas. Une pièce nommée tombe à coup sûr,
            une seule fois, et il faut aller la prendre là où elle est.
          </p>
          {erreur && <p className="erreur">{erreur}</p>}
          {note && <p className="note">{note}</p>}
        </div>

        <section>
          <Codex
            donjons={donjons}
            surEntrer={(d) => void agir(() => api.entrerDonjon(id, d.code))}
          />
        </section>
      </div>
    )
  }

  // ─── Dedans ───────────────────────────────────────────────────────────────

  const e = expedition
  const etage = e.etage

  return (
    <div className="cadre">
      <div className="entete">
        <div className="surtitre">
          {e.donjon} · étage {e.progression.numero} sur {e.progression.total}
        </div>
        <h1>{etage.nom}</h1>
        {etage.texte && <p className="attenue" style={{ maxWidth: '62ch' }}>{etage.texte}</p>}
        {erreur && <p className="erreur">{erreur}</p>}
        {note && <p className="note">{note}</p>}
      </div>

      <section>
        <div className="surtitre">{NOMS_TYPE[etage.type]}</div>

        {e.combatEnCours && (
          <>
            <p>Il y a quelqu’un devant toi.</p>
            <button className="action" onClick={surCombat}>Aller au combat</button>
          </>
        )}

        {!e.combatEnCours && (etage.type === 'rencontre' || etage.type === 'boss') && (
          <>
            {etage.adversaire && (
              <div className="paires" style={{ maxWidth: 560, marginBottom: 18 }}>
                <div className="paire">
                  <b>{etage.type === 'boss' ? 'Au bout' : 'Devant'}</b>
                  <span className="chiffre">
                    {etage.adversaire.nom}
                    <span className="tenu"> · niveau {etage.adversaire.niveau}</span>
                  </span>
                </div>
              </div>
            )}
            {etage.adversaire?.texte && (
              <p className="attenue" style={{ maxWidth: '62ch' }}>{etage.adversaire.texte}</p>
            )}
            <button className="action" onClick={() => void agir(() => api.avancerEtage(id))}>
              Engager
            </button>
          </>
        )}

        {!e.combatEnCours && etage.type === 'passage' && etage.exigence && (
          <>
            <p className="attenue" style={{ maxWidth: '62ch' }}>
              Il faut {etage.exigence.seuil} en {NOMS_STATS[etage.exigence.stat]}. Tu en as{' '}
              {etage.exigence.valeur}. On passe dans tous les cas, mais rater se paie.
            </p>
            <button className="action" onClick={() => void agir(
              () => api.avancerEtage(id),
              (r) => {
                const x = r as { reussi: boolean; texte: string | null; degats: number }
                if (x.reussi) return x.texte ?? 'Tu passes.'
                return `${x.texte ?? 'Tu passes mal.'} ${x.degats} points de vie.`
              },
            )}>
              Franchir
            </button>
          </>
        )}

        {!e.combatEnCours && etage.type === 'trouvaille' && (
          <button className="action" onClick={() => void agir(
            () => api.avancerEtage(id),
            (r) => {
              const x = r as { or: number }
              return x.or > 0 ? `${x.or} or de plus dans le sac.` : 'Rien qui vaille la peine.'
            },
          )}>
            Fouiller
          </button>
        )}

        {!e.combatEnCours && etage.type === 'palier' && (
          <>
            <p className="attenue" style={{ maxWidth: '62ch' }}>
              Tu peux t’arrêter là et ressortir avec ce que tu as. Après, non.
            </p>
            <button className="action" onClick={() => void agir(() => api.avancerEtage(id))}>
              Continuer
            </button>
          </>
        )}

        {!e.combatEnCours && etage.type === 'carrefour' && etage.options && (
          <div className="choix-etage">
            {etage.options.map((o) => (
              <button
                key={o.code}
                className="choix"
                disabled={!o.ouverte}
                onClick={() => void agir(() => api.avancerEtage(id, o.code))}
              >
                <span>{o.texte}</span>
                {!o.ouverte && <span className="empechement">{o.empechement}</span>}
              </button>
            ))}
          </div>
        )}
      </section>

      <section>
        <div className="surtitre">Le sac</div>
        <p className="attenue" style={{ maxWidth: '62ch' }}>
          {e.sac.or} or
          {e.sac.or === 0 && e.sac.pieces.length === 0 && ' pour l’instant'}
        </p>
        {/* Ce qu'on porte, avec son image : le sac ne listait que des noms. */}
        {e.sac.pieces.length > 0 && (
          <div className="sac-pieces">
            {e.sac.pieces.map((piece, i) => (
              <span className="sac-piece" key={`${piece.code}-${i}`}>
                <IconeObjet objet={piece} taille={26} />
                <NomObjet
                  o={piece}
                  balise="span"
                  suffixe={
                    <>
                      <TypeObjet o={piece} className="objet-type objet-type-ligne" />
                      <EffetObjet effet={piece.effetTexte} />
                    </>
                  }
                />
              </span>
            ))}
          </div>
        )}
        {e.engage ? (
          <p className="urgence">
            Tu es allé trop loin pour repartir. Ça se finit ou ça tombe.
          </p>
        ) : (
          <button
            className="discret"
            onClick={() => void agir(
              () => api.ressortir(id),
              (r) => {
                const x = r as { or: number; pieces: number }
                return `Ressorti avec ${x.or} or et ${x.pieces} pièce${x.pieces > 1 ? 's' : ''}.`
              },
            )}
          >
            Ressortir avec ça
          </button>
        )}
      </section>
    </div>
  )
}
